import type { PublishQuestionnaireTemplateVersionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  PublishQuestionnaireTemplateVersionDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface PublishQuestionnaireTemplateVersionInput {
  QuestionnaireTemplateId: string;
  QuestionnaireTemplateVersionId: string;
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation
 * response shape so consumers branching on the result do not need
 * to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): PublishQuestionnaireTemplateVersionMutation => ({
  publishQuestionnaireTemplateVersion: {
    __typename: 'GenericMutationOutput',
    affected_rows: data.affected_rows,
  },
});

/**
 * Flag-gated wrapper for the `publishQuestionnaireTemplateVersion`
 * mutation. Defaults to the legacy Apollo mutation and delegates to
 * the tRPC variant when the `trpc` feature flag is enabled.
 */
export const usePublishQuestionnaireTemplateVersion = createMutationHook({
  name: 'publishQuestionnaireTemplateVersion',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.questionnaireTemplateVersion.publish.mutationOptions({
      onSuccess,
    }),
  mapInput: (input: PublishQuestionnaireTemplateVersionInput) => ({
    QuestionnaireTemplateId: input.QuestionnaireTemplateId,
    QuestionnaireTemplateVersionId: input.QuestionnaireTemplateVersionId,
  }),
  invalidate: (trpc) => [
    trpc.frontend.questionnaireTemplateVersion.pathKey(),
    trpc.frontend.questionnaireTemplate.pathKey(),
  ],
  mapResponse: (result: {
    affected_rows: number;
  }): PublishQuestionnaireTemplateVersionMutation =>
    mapTrpcResponseToGraphQL(result),
  graphqlDocument: PublishQuestionnaireTemplateVersionDocument,
  graphqlVariables: (input: PublishQuestionnaireTemplateVersionInput) => ({
    questionnaireTemplateId: input.QuestionnaireTemplateId,
    questionnaireTemplateVersionId: input.QuestionnaireTemplateVersionId,
  }),
  evictFields: [
    'questionnaire_template_version',
    'questionnaire_template',
    'questionnaire_template_by_pk',
  ],
  refetchQueries: [
    namedOperations.Query.getQuestionnaireTemplateById,
    namedOperations.Query.getQuestionnaireTemplates,
  ],
});
